import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Users, Sparkles, Loader2, ImagePlus, X, AlertTriangle } from "lucide-react";

const TagInput = ({ label, hint, value, onChange, testid }) => {
  const [text, setText] = useState("");
  const add = () => { const v = text.trim(); if (v && !value.includes(v)) onChange([...value, v]); setText(""); };
  return (
    <div>
      <label className="text-sm font-medium text-slate-800">{label}</label>
      {hint && <p className="text-xs text-slate-400 mb-1">{hint}</p>}
      <div className="flex flex-wrap gap-1 mb-2">
        {value.map((t, i) => (
          <span key={i} className="text-xs bg-slate-100 px-2 py-1 rounded-full inline-flex items-center gap-1">
            {t}<button type="button" onClick={() => onChange(value.filter((x) => x !== t))} className="text-slate-400 hover:text-rose-500">×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Ajouter puis Entrée…" data-testid={testid} />
        <Button type="button" variant="outline" onClick={add}>Ajouter</Button>
      </div>
    </div>
  );
};

export default function ProNetworkProfile() {
  const [biz, setBiz] = useState(null);
  const [form, setForm] = useState({ headline: "", bio: "", cherche: [], propose: [], zone: "", accept_contact: true, images: [] });
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/pro/network/me").then((r) => {
      setBiz(r.data.business);
      const p = r.data.profile || {};
      const empty = !p.headline && !p.bio && (p.cherche || []).length === 0 && (p.propose || []).length === 0;
      setIsNew(!!r.data.business && empty);
      setForm((f) => ({
        ...f, ...p,
        cherche: p.cherche || [],
        propose: p.propose || [],
        images: p.images || [],
        zone: p.zone || r.data.business?.city || "",
      }));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/pro/network/me", form); toast.success("Profil réseau enregistré ✅"); setNotice(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const aiFill = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/pro/network/ai-suggest");
      const s = data.suggestion || {};
      setForm((f) => ({
        ...f,
        headline: s.headline || f.headline,
        bio: s.bio || f.bio,
        cherche: (s.cherche && s.cherche.length) ? s.cherche : f.cherche,
        propose: (s.propose && s.propose.length) ? s.propose : f.propose,
        zone: s.zone || f.zone,
      }));
      setNotice(data.notice || "Texte généré par l'IA — relisez-le et corrigez-le avant d'enregistrer.");
      toast.success("Proposition générée — à relire avant d'enregistrer");
    } catch (e) { toast.error(e?.response?.data?.detail || "Génération impossible"); }
    finally { setAiLoading(false); }
  };

  const onPickImages = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = 3 - (form.images || []).length;
    if (room <= 0) { toast.error("3 images maximum"); return; }
    if (files.length > room) toast.message(`Seules ${room} image(s) seront ajoutées (3 maximum).`);
    setUploading(true);
    try {
      const urls = [];
      for (const file of files.slice(0, room)) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/pro/fb-posts/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data?.url) urls.push(data.url);
      }
      setForm((f) => ({ ...f, images: [...(f.images || []), ...urls].slice(0, 3) }));
      if (urls.length) toast.success("Image(s) ajoutée(s)");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload impossible"); }
    finally { setUploading(false); }
  };
  const removeImage = (url) => setForm((f) => ({ ...f, images: (f.images || []).filter((u) => u !== url) }));

  return (
    <div className="max-w-2xl space-y-6" data-testid="pro-network-profile">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Mon profil réseau</h1>
        <p className="text-slate-500 text-sm mt-1">Précisez ce que vous cherchez et proposez pour attirer les bons partenaires du 28.</p>
      </div>

      {!biz && <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-xl p-3">Créez d'abord votre mini-site (fiche entreprise) pour rejoindre le réseau.</div>}

      {biz && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="netprof-biz-banner">
          <div className="flex items-center gap-3">
            {biz.logo ? <img src={biz.logo} alt="" className="w-11 h-11 rounded-xl object-cover border border-emerald-200" /> : <div className="w-11 h-11 rounded-xl bg-white border border-emerald-200 flex items-center justify-center text-emerald-500"><Users size={18} /></div>}
            <div className="min-w-0">
              <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Vous êtes déjà partenaire</div>
              <div className="font-bold text-slate-900 truncate">{biz.name}{biz.city ? ` · ${biz.city}` : ""}</div>
            </div>
          </div>
          <p className="text-sm text-emerald-800 mt-2">
            {isNew
              ? "Il ne s'agit pas d'une inscription : votre compte pro est déjà actif. Renseignez simplement les champs ci-dessous pour activer votre visibilité dans le Réseau Pro local."
              : "Votre profil réseau est actif. Vous pouvez le mettre à jour à tout moment."}
          </p>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-gradient-to-br from-violet-50 to-white border border-violet-200 rounded-xl p-3">
          <div className="flex-1">
            <div className="font-semibold text-slate-900 text-sm inline-flex items-center gap-1.5"><Sparkles size={15} className="text-violet-600" /> Pré-remplir avec l'IA</div>
            <p className="text-xs text-slate-500 mt-0.5">Génère une proposition à partir de votre activité. <strong>À relire et corriger</strong> avant d'enregistrer.</p>
          </div>
          <Button type="button" variant="outline" onClick={aiFill} disabled={aiLoading || !biz} className="border-violet-300 text-violet-700 hover:bg-violet-50 shrink-0" data-testid="netprof-ai-fill">
            {aiLoading ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Sparkles size={15} className="mr-1.5" />}{aiLoading ? "Génération…" : "Générer"}
          </Button>
        </div>
        {notice && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg p-2.5 flex items-start gap-2" data-testid="netprof-ai-notice">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {notice}
          </div>
        )}
        <div>
          <label className="text-sm font-medium text-slate-800">Accroche</label>
          <Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="Ex. Plombier chauffagiste, cherche artisans pour chantiers communs" data-testid="netprof-headline" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-800">Présentation</label>
          <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={4} placeholder="Présentez votre activité, vos spécialités, vos envies de collaboration…" data-testid="netprof-bio" />
        </div>
        <TagInput label="Je cherche" hint="Partenaires, fournisseurs, compétences recherchées" value={form.cherche} onChange={(v) => setForm({ ...form, cherche: v })} testid="netprof-cherche" />
        <TagInput label="Je propose" hint="Services, produits, savoir-faire que vous offrez aux autres pros" value={form.propose} onChange={(v) => setForm({ ...form, propose: v })} testid="netprof-propose" />
        <div>
          <label className="text-sm font-medium text-slate-800">Zone d'intervention</label>
          <Input value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} placeholder="Ex. Chartres et agglo, tout le 28…" data-testid="netprof-zone" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" checked={form.accept_contact} onChange={(e) => setForm({ ...form, accept_contact: e.target.checked })} data-testid="netprof-accept" />
          Partager mes coordonnées avec mes pros connectés
        </label>
        <div>
          <label className="text-sm font-medium text-slate-800">Photos (jusqu'à 3)</label>
          <p className="text-xs text-slate-400 mb-2">Réalisations, équipe, boutique… pour donner confiance aux autres pros.</p>
          <div className="flex flex-wrap gap-3">
            {(form.images || []).map((url) => (
              <div key={url} className="relative w-24 h-24 rounded-xl overflow-hidden border border-slate-200 group" data-testid="netprof-image">
                <img src={url} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(url)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white grid place-items-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" data-testid="netprof-image-remove"><X size={13} /></button>
              </div>
            ))}
            {(form.images || []).length < 3 && (
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-300 text-slate-400 hover:border-[#E05638] hover:text-[#E05638] grid place-items-center transition-colors" data-testid="netprof-image-add">
                {uploading ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={22} />}
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onPickImages} data-testid="netprof-image-input" />
        </div>
        <Button onClick={save} disabled={saving || !biz} className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="netprof-save"><Save size={16} className="mr-2" />{saving ? "Enregistrement…" : isNew ? "Activer ma visibilité réseau" : "Enregistrer"}</Button>
      </div>
    </div>
  );
}
